// Bluepill-styled segment breakdown bar chart (recharts), themed from tokens.

import { useEffect, useRef, useState } from 'react';
import {
  BarChart as RBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { SegmentBarItem } from './types';

const TEAL = '#07E5D1';
const GRID = '#EEF2F6';
const AXIS = '#94A3B8';

// Chart geometry
const ROW_HEIGHT = 40;
const MIN_CHART_HEIGHT = 120;
const CHART_V_PADDING = 40;

interface Props {
  title?: string;
  items?: SegmentBarItem[];
}

const SegmentBarChart: React.FC<Props> = ({ title, items }) => {
  const ref = useRef<HTMLDivElement>(null);
  // recharts needs a concrete color, not a CSS var attribute — read the resolved
  // accent token from the cascade (falls back to teal).
  const [accent, setAccent] = useState(TEAL);
  useEffect(() => {
    if (ref.current) {
      const v = getComputedStyle(ref.current).getPropertyValue('--insight-accent').trim();
      if (v) setAccent(v);
    }
  }, []);

  // Coerce stringified numbers and clamp to [0,100] (parity with ThemeBars).
  const data = (items || [])
    .map((d) => (d ? { ...d, pct: Math.max(0, Math.min(100, Number(d.pct))) } : d))
    .filter((d): d is SegmentBarItem => !!d && Number.isFinite(d.pct));
  if (data.length === 0) return null;

  const height = Math.max(MIN_CHART_HEIGHT, data.length * ROW_HEIGHT + CHART_V_PADDING);

  return (
    <div
      ref={ref}
      className="my-3 rounded-[var(--insight-radius)] border bg-[var(--insight-card)] p-4"
      style={{ borderColor: 'var(--insight-border)', boxShadow: 'var(--insight-shadow)' }}
    >
      {title && (
        <p className="text-sm font-semibold mb-3" style={{ color: 'var(--insight-ink)' }}>
          {title}
        </p>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <RBarChart data={data} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 8 }}>
          <CartesianGrid horizontal={false} stroke={GRID} />
          <XAxis
            type="number"
            domain={[0, 100]}
            unit="%"
            tick={{ fill: AXIS, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="segment"
            width={140}
            tick={{ fill: '#374151', fontSize: 12 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: 'rgba(7,229,209,0.06)' }}
            formatter={(v: number, _n, p) => {
              const n = (p?.payload as SegmentBarItem)?.n;
              return [`${v}%${n != null ? ` (n=${n})` : ''}`, 'Share'];
            }}
            contentStyle={{ borderRadius: 12, border: `1px solid ${GRID}`, fontSize: 12 }}
          />
          <Bar dataKey="pct" radius={[0, 6, 6, 0]} barSize={18} isAnimationActive={false}>
            {data.map((_, i) => (
              <Cell key={i} fill={accent} />
            ))}
          </Bar>
        </RBarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SegmentBarChart;
